'use client';

import { useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface SolutionDetail {
  title: string;
  description: string;
  image: string;
  alt: string;
  features: string[];
  industries: string[];
  fabricOptions: string[];
  customizationOptions: string[];
  qualityStandards: string[];
  leadTime: string;
  minimumOrder: string;
}

interface SolutionDetailModalProps {
  solution: SolutionDetail | null;
  isOpen: boolean;
  onClose: () => void;
  onRequestConsultation: () => void;
}

export default function SolutionDetailModal({
  solution,
  isOpen,
  onClose,
  onRequestConsultation,
}: SolutionDetailModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !solution) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-card rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-headline font-semibold text-primary">
            {solution.title}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} variant="outline" className="text-text-secondary" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="relative h-80 overflow-hidden rounded-lg mb-6">
            <AppImage
              src={solution.image}
              alt={solution.alt}
              className="w-full h-full object-cover"
            />
          </div>
          
          <p className="text-text-secondary font-body mb-6 leading-relaxed text-lg">
            {solution.description}
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-lg font-headline font-semibold text-primary mb-3 flex items-center">
                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success mr-2" />
                Complete Features
              </h3>
              <ul className="space-y-2">
                {solution.features.map((feature, index) => (
                  <li key={index} className="text-text-secondary flex items-start">
                    <Icon name="ChevronRightIcon" size={16} variant="outline" className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-headline font-semibold text-primary mb-3 flex items-center">
                <Icon name="SwatchIcon" size={20} variant="outline" className="text-brand-gold mr-2" />
                Fabric Options
              </h3>
              <ul className="space-y-2">
                {solution.fabricOptions.map((fabric, index) => (
                  <li key={index} className="text-text-secondary flex items-start">
                    <Icon name="ChevronRightIcon" size={16} variant="outline" className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>{fabric}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-lg font-headline font-semibold text-primary mb-3 flex items-center">
                <Icon name="PaintBrushIcon" size={20} variant="outline" className="text-brand-gold mr-2" />
                Customization Options
              </h3>
              <ul className="space-y-2">
                {solution.customizationOptions.map((option, index) => (
                  <li key={index} className="text-text-secondary flex items-start">
                    <Icon name="ChevronRightIcon" size={16} variant="outline" className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>{option}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-headline font-semibold text-primary mb-3 flex items-center">
                <Icon name="ShieldCheckIcon" size={20} variant="solid" className="text-success mr-2" />
                Quality Standards
              </h3>
              <ul className="space-y-2">
                {solution.qualityStandards.map((standard, index) => (
                  <li key={index} className="text-text-secondary flex items-start">
                    <Icon name="ChevronRightIcon" size={16} variant="outline" className="text-accent mt-1 mr-2 flex-shrink-0" />
                    <span>{standard}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="bg-muted rounded-lg p-4 mb-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <Icon name="ClockIcon" size={20} variant="outline" className="text-primary mr-3" />
                <div>
                  <p className="text-sm font-semibold text-text-primary">Lead Time</p>
                  <p className="text-text-secondary">{solution.leadTime}</p>
                </div>
              </div>
              <div className="flex items-center">
                <Icon name="ShoppingBagIcon" size={20} variant="outline" className="text-primary mr-3" />
                <div>
                  <p className="text-sm font-semibold text-text-primary">Minimum Order</p>
                  <p className="text-text-secondary">{solution.minimumOrder}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={onRequestConsultation}
              className="flex-1 px-6 py-3 bg-brand-gold text-brand-gold-foreground font-cta font-semibold rounded-md hover:bg-opacity-90 transition-all duration-300 shadow-subtle flex items-center justify-center"
            >
              <Icon name="CalendarIcon" size={20} variant="outline" className="mr-2" />
              Request Consultation
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-secondary text-secondary-foreground font-cta font-semibold rounded-md hover:bg-opacity-90 transition-all duration-300 shadow-subtle"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}